import React from 'react';
import { X } from 'lucide-react';
import { MarkdownWithLinks } from '../MarkdownWithLinks';

const OTHER = '__other__';

/**
 * One worksheet field. Kept dumb on purpose: the fill screen owns the answers and the autosave.
 *
 * A choice answer is stored as `{ value, other }` so "Other ___" survives a reload.
 */
export function WorksheetFieldInput({ field, value, onChange, missing }) {
  const base =
    'w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 ' +
    'focus:outline-none focus:ring-2 focus:ring-cyan-500/40 transition-shadow';
  const border = missing ? 'border-red-400' : 'border-gray-200';
  const testid = `worksheet-field-${field.id}`;

  if (field.type === 'heading') {
    return (
      <div className="pt-6 first:pt-0" data-testid={testid}>
        <h4 className="font-serif text-xl text-gray-900">
          <MarkdownWithLinks inline>{field.label}</MarkdownWithLinks>
        </h4>
        {field.help && (
          <p className="text-sm text-gray-500 mt-1">
            <MarkdownWithLinks inline>{field.help}</MarkdownWithLinks>
          </p>
        )}
        <div className="h-px bg-gray-200 mt-3" />
      </div>
    );
  }

  if (field.type === 'table') {
    const columns = field.table_columns || [];
    const rows = field.table_rows || [];
    return (
      <div className="space-y-3" data-testid={testid}>
        {field.label && (
          <p className="font-serif text-lg text-gray-900">
            <MarkdownWithLinks inline>{field.label}</MarkdownWithLinks>
          </p>
        )}

        {/* Desktop: the real table, scrolling sideways if it is wider than the page. */}
        <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm" data-testid={`worksheet-table-${field.id}`}>
            <thead className="bg-gray-50">
              <tr>
                {columns.map((header, c) => (
                  <th key={c} className="px-3 py-2 text-left font-semibold text-gray-800
                                         border-b border-gray-200 align-top">
                    <MarkdownWithLinks inline>{header}</MarkdownWithLinks>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r} className="odd:bg-white even:bg-gray-50/60">
                  {columns.map((_, c) => (
                    <td key={c} className="px-3 py-2 align-top text-gray-700
                                           border-b border-gray-100">
                      <MarkdownWithLinks inline>{row[c] || ''}</MarkdownWithLinks>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Phone: one card per row, so six columns stay readable without pinching. */}
        <div className="md:hidden space-y-3" data-testid={`worksheet-table-cards-${field.id}`}>
          {rows.map((row, r) => (
            <div key={r} className="rounded-lg border border-gray-200 divide-y divide-gray-100">
              {columns.map((header, c) => (
                <div key={c} className="px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">
                    <MarkdownWithLinks inline>{header}</MarkdownWithLinks>
                  </p>
                  <p className="text-sm text-gray-700">
                    <MarkdownWithLinks inline>{row[c] || '—'}</MarkdownWithLinks>
                  </p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'instruction') {
    return (
      <div className="space-y-4 text-[15px] leading-relaxed text-gray-700" data-testid={testid}>
        {/* A blank line between his lines means a new paragraph — it used to be thrown away,
            leaving every line equally spaced and forcing him to invent separators. */}
        {(field.label || '').split(/\n\s*\n/).filter((block) => block.trim()).map((block, i) => (
          <div key={i}><MarkdownWithLinks inline>{block.trim()}</MarkdownWithLinks></div>
        ))}
      </div>
    );
  }

  const choiceValue = (value && typeof value === 'object') ? value : { value: value || '', other: '' };
  const setChoice = (next) => onChange({ ...choiceValue, ...next });

  const label = (
    <div className="mb-2">
      <label className="block text-sm font-medium text-gray-800">
        <MarkdownWithLinks inline>{field.label}</MarkdownWithLinks>
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {field.help && (
        <div className="text-xs text-gray-500 mt-0.5">
          <MarkdownWithLinks className="prose-xs">{field.help}</MarkdownWithLinks>
        </div>
      )}
    </div>
  );

  return (
    <div data-testid={testid}>
      {label}

      {field.type === 'signature' && (() => {
        // Typed name plus a tick, stamped with the moment they ticked it — the owner's choice
        // of what a waiver needs (Jun 2026). The stamp is set here rather than on the server
        // so the client can see exactly what is being recorded against their name.
        const sig = (value && typeof value === 'object') ? value : {};
        const set = (patch) => {
          const next = { ...sig, ...patch };
          const signed = String(next.name || '').trim() && next.agreed;
          onChange({ ...next, signed_at: signed ? (next.signed_at || new Date().toISOString()) : '' });
        };
        const stamp = sig.signed_at ? new Date(sig.signed_at) : null;
        return (
          <div className="space-y-2" data-testid={`worksheet-signature-${field.id}`}>
            <input
              className={`${base} ${border} font-serif text-base`}
              value={sig.name || ''}
              placeholder="Type your full name"
              onChange={(e) => set({ name: e.target.value })}
              data-testid={`worksheet-signature-name-${field.id}`}
            />
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!sig.agreed}
                onChange={(e) => set({ agreed: e.target.checked })}
                data-testid={`worksheet-signature-agree-${field.id}`}
              />
              <span>
                I agree, and typing my name above is my signature.
              </span>
            </label>
            {stamp && !Number.isNaN(stamp.getTime()) && (
              <p className="text-xs text-gray-500" data-testid={`worksheet-signature-stamp-${field.id}`}>
                Signed {stamp.toLocaleString()}
              </p>
            )}
          </div>
        );
      })()}

      {field.type === 'short_text' && (
        <input className={`${base} ${border}`} value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`worksheet-input-${field.id}`} />
      )}

      {field.type === 'paragraph' && (
        <textarea className={`${base} ${border} leading-relaxed`} rows={field.rows || 4}
          value={value || ''} onChange={(e) => onChange(e.target.value)}
          data-testid={`worksheet-input-${field.id}`} />
      )}

      {field.type === 'list' && (
        <div className="space-y-2" data-testid={`worksheet-list-${field.id}`}>
          {Array.from({ length: Math.max(1, field.list_count || 3) }, (_, i) => i).map((i) => {
            const entries = Array.isArray(value) ? value : [];
            const setEntry = (text) => {
              const next = [...entries];
              while (next.length < (field.list_count || 3)) next.push('');
              next[i] = text;
              onChange(next);
            };
            return (
              <div key={i} className="flex items-start gap-2">
                <span className="w-6 pt-2 text-sm text-gray-400 tabular-nums text-right">{i + 1}.</span>
                {(field.rows || 1) > 1 ? (
                  <textarea className={`${base} ${border} leading-relaxed`} rows={field.rows}
                    value={entries[i] || ''} onChange={(e) => setEntry(e.target.value)}
                    data-testid={`worksheet-list-input-${field.id}-${i + 1}`} />
                ) : (
                  <input className={`${base} ${border}`} value={entries[i] || ''}
                    onChange={(e) => setEntry(e.target.value)}
                    data-testid={`worksheet-list-input-${field.id}-${i + 1}`} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {field.type === 'table_fill' && (() => {
        const columns = field.table_columns || [];
        const types = field.table_column_types || [];
        const columnOptions = field.table_column_options || [];
        const start = Math.max(1, field.table_start_rows || 3);
        const rows = (Array.isArray(value) && value.length)
          ? value
          : Array.from({ length: start }, () => columns.map(() => ''));

        const setCell = (r, c, text) => {
          const next = rows.map((row) => [...row]);
          while (next[r].length < columns.length) next[r].push('');
          next[r][c] = text;
          onChange(next);
        };
        const addRow = () => onChange([...rows, columns.map(() => '')]);
        const removeRow = (r) => onChange(rows.filter((_, i) => i !== r));

        const cellInput = (r, c, where = 'desktop') => {
          const type = types[c] || 'text';
          const shared = `${base} ${border} text-sm`;
          // The phone layout renders alongside the table (CSS hides one), so the ids must differ.
          const testid = where === 'desktop'
            ? `worksheet-fill-cell-${field.id}-${r}-${c}`
            : `worksheet-fill-cell-m-${field.id}-${r}-${c}`;
          if (type === 'choice' && (columnOptions[c] || []).length) {
            return (
              <select className={shared} value={rows[r][c] || ''}
                onChange={(e) => setCell(r, c, e.target.value)} data-testid={testid}>
                <option value="">—</option>
                {columnOptions[c].map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            );
          }
          return (
            <input className={shared} type={type === 'number' ? 'number' : 'text'}
              value={rows[r][c] || ''} onChange={(e) => setCell(r, c, e.target.value)}
              data-testid={testid} />
          );
        };

        return (
          <div className="space-y-2" data-testid={`worksheet-fill-table-${field.id}`}>
            {/* Desktop: the grid as printed. */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm border-separate border-spacing-1">
                <thead>
                  <tr>
                    {columns.map((header, c) => (
                      <th key={c} className="px-1 pb-1 text-left align-bottom text-xs
                                             font-semibold text-gray-600">
                        {header}
                      </th>
                    ))}
                    <th className="w-6" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((_, r) => (
                    <tr key={r}>
                      {columns.map((__, c) => <td key={c} className="align-top">{cellInput(r, c)}</td>)}
                      <td className="align-middle">
                        {rows.length > 1 && (
                          <button type="button" onClick={() => removeRow(r)}
                            className="text-gray-300 hover:text-red-500"
                            title="Remove this row"
                            data-testid={`worksheet-fill-remove-row-${field.id}-${r}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Phone: one card per row, labels above each box. */}
            <div className="md:hidden space-y-3">
              {rows.map((_, r) => (
                <div key={r} className="rounded-lg border border-gray-200 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] uppercase tracking-wide text-gray-400">
                      Row {r + 1}
                    </p>
                    {rows.length > 1 && (
                      <button type="button" onClick={() => removeRow(r)}
                        className="text-gray-300 hover:text-red-500"
                        data-testid={`worksheet-fill-remove-row-mobile-${field.id}-${r}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {columns.map((header, c) => (
                    <div key={c} className="space-y-1">
                      <p className="text-xs text-gray-500">{header}</p>
                      {cellInput(r, c, 'phone')}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <button type="button" onClick={addRow}
              className="text-xs text-gray-500 hover:text-gray-800 underline"
              data-testid={`worksheet-fill-add-row-${field.id}`}>
              + Add another row
            </button>
          </div>
        );
      })()}

      {field.type === 'date' && (
        <input type="date" className={`${base} ${border}`} value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`worksheet-input-${field.id}`} />
      )}

      {field.type === 'dropdown' && (
        <select className={`${base} ${border}`} value={choiceValue.value}
          onChange={(e) => setChoice({ value: e.target.value })}
          data-testid={`worksheet-input-${field.id}`}>
          <option value="">Choose…</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          {field.allow_other && <option value={OTHER}>Other…</option>}
        </select>
      )}

      {field.type === 'multiple_choice' && (
        <div className="space-y-2">
          {(field.options || []).map((o) => (
            <label key={o} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="radio" name={field.id} checked={choiceValue.value === o}
                onChange={() => setChoice({ value: o })}
                data-testid={`worksheet-option-${field.id}-${o.toLowerCase().replace(/\s+/g, '-')}`} />
              {o}
            </label>
          ))}
          {field.allow_other && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="radio" name={field.id} checked={choiceValue.value === OTHER}
                onChange={() => setChoice({ value: OTHER })}
                data-testid={`worksheet-option-${field.id}-other`} />
              Other
            </label>
          )}
        </div>
      )}

      {field.type === 'checkbox' && (
        <div className="space-y-2">
          {(field.options || []).map((o) => {
            const list = Array.isArray(value) ? value : [];
            return (
              <label key={o} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={list.includes(o)}
                  onChange={() => onChange(list.includes(o)
                    ? list.filter((v) => v !== o) : [...list, o])}
                  data-testid={`worksheet-check-${field.id}-${o.toLowerCase().replace(/\s+/g, '-')}`} />
                {o}
              </label>
            );
          })}
        </div>
      )}

      {field.type === 'scale' && (
        <div>
          <div className="flex gap-1.5 flex-wrap">
            {Array.from({ length: (field.scale_max || 5) - (field.scale_min || 1) + 1 },
              (_, i) => (field.scale_min || 1) + i).map((n) => (
              <button key={n} type="button" onClick={() => onChange(n)}
                data-testid={`worksheet-scale-${field.id}-${n}`}
                className={`w-9 h-9 rounded-full text-sm border transition-colors ${
                  value === n
                    ? 'bg-cyan-600 border-cyan-600 text-white'
                    : 'border-gray-200 text-gray-600 hover:border-cyan-400'}`}>
                {n}
              </button>
            ))}
          </div>
          {(field.min_label || field.max_label) && (
            <div className="flex justify-between text-xs text-gray-400 mt-1.5">
              <span>{field.min_label}</span><span>{field.max_label}</span>
            </div>
          )}
        </div>
      )}

      {field.allow_other && choiceValue.value === OTHER && (
        <input className={`${base} ${border} mt-2`} placeholder="Tell us"
          value={choiceValue.other || ''} onChange={(e) => setChoice({ other: e.target.value })}
          data-testid={`worksheet-other-${field.id}`} />
      )}

      {missing && <p className="text-xs text-red-500 mt-1">This one is still needed</p>}
    </div>
  );
}

export default WorksheetFieldInput;
